/**
 * @praesto/core/i18n
 *
 * i18next-backed implementation of `I18nService`. Modules register their
 * translation bundles at load time via `registerModuleTranslations`.
 *
 * Keys are flat, dotted, module-prefixed strings (e.g. "procedure.title"),
 * so the instance runs with `keySeparator`/`nsSeparator` disabled — a key is
 * looked up literally, never split into a nested path.
 *
 * Contract: packages/contracts/src/core-api.ts (I18nService).
 */
import i18next, { type i18n as I18nInstance } from "i18next";
import type { I18nService } from "@praesto/contracts";
import type { Locale } from "@praesto/shared-types";

const SHARED_NS = "translation";

export function createI18nService(opts?: { readonly defaultLocale?: Locale }): I18nService {
  const lng: Locale = opts?.defaultLocale ?? "it";
  const instance: I18nInstance = i18next.createInstance();

  void instance.init({
    lng,
    fallbackLng: "en",
    ns: [SHARED_NS],
    defaultNS: SHARED_NS,
    resources: { it: { [SHARED_NS]: {} }, en: { [SHARED_NS]: {} } },
    keySeparator: false,
    nsSeparator: false,
    interpolation: { escapeValue: false },
  });

  return {
    t(key: string, params?: Readonly<Record<string, string | number>>): string {
      return instance.t(key, (params ?? {}) as Record<string, unknown>);
    },

    locale(): Locale {
      return (instance.resolvedLanguage ?? instance.language ?? lng) as Locale;
    },

    registerModuleTranslations(
      _moduleId,
      translations: Readonly<Record<Locale, Readonly<Record<string, string>>>>,
    ): void {
      for (const [loc, bundle] of Object.entries(translations)) {
        // deep-merge + overwrite: later registrations win on key collisions.
        instance.addResourceBundle(loc, SHARED_NS, bundle, true, true);
      }
    },
  };
}
